import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  IconButton,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { getOrderPublicForm, submitOrderPublicForm, uploadOrderPublicFormMedia } from '../../../app/api';
import { translate as translateI18n } from '../../../app/i18n';
import { useTranslation } from '../../../app/LanguageContext';
import { SavingOverlay } from '../../core/public/SavingOverlay';
import ThemeModeToggle from '../../core/public/ThemeModeToggle';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

function isAcceptedImageFile(file) {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  if (ACCEPTED_IMAGE_TYPES.includes(type)) return true;
  return type.startsWith('image/');
}

function extractImageFilesFromDataTransfer(dataTransfer) {
  if (!dataTransfer) return [];
  const fromList = dataTransfer.files ? Array.from(dataTransfer.files) : [];
  if (fromList.length) return fromList.filter(isAcceptedImageFile);
  const fromItems = dataTransfer.items ? Array.from(dataTransfer.items) : [];
  const out = [];
  fromItems.forEach((item) => {
    if (item.kind !== 'file') return;
    const file = item.getAsFile();
    if (isAcceptedImageFile(file)) out.push(file);
  });
  return out;
}

function parsePublicImageIds(raw) {
  if (raw == null || String(raw).trim() === '') return [];
  try {
    const v = JSON.parse(String(raw));
    return Array.isArray(v) ? v.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function stringifyPublicImageIds(ids) {
  return JSON.stringify(ids || []);
}

const OrderPublicFormPage = () => {
  const { workspaceId, token } = useParams();
  const { translate, setLanguage } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [blockingError, setBlockingError] = useState(null);
  const [surfaceError, setSurfaceError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState(null);
  const [lineItemValues, setLineItemValues] = useState({});
  const [publicImagePathById, setPublicImagePathById] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragOverImageKey, setDragOverImageKey] = useState(null);
  const publicImageFileInputRef = useRef(null);
  const publicImageUploadTargetRef = useRef({ itemId: null, key: null });
  const lineItemValuesRef = useRef(lineItemValues);

  useEffect(() => {
    lineItemValuesRef.current = lineItemValues;
  }, [lineItemValues]);

  const imageFieldTargets = useMemo(() => {
    const targets = [];
    (formData?.line_items || []).forEach((li) => {
      (li.customization_fields || []).forEach((f) => {
        if (f.value_type === 'image') {
          targets.push({ itemId: li.item_id, key: f.key });
        }
      });
    });
    return targets;
  }, [formData]);

  useEffect(() => {
    if (imageFieldTargets.length === 1) {
      publicImageUploadTargetRef.current = imageFieldTargets[0];
    }
  }, [imageFieldTargets]);

  const setActiveImageTarget = useCallback((itemId, key) => {
    publicImageUploadTargetRef.current = { itemId, key };
  }, []);

  const imageFieldKey = useCallback((itemId, key) => `${itemId}:${key}`, []);

  useEffect(() => {
    const load = async () => {
      const nav = typeof navigator !== 'undefined' ? navigator.language || '' : '';
      const lang = nav.toLowerCase().startsWith('pt') ? 'pt-BR' : 'en-US';
      await setLanguage(lang, false);
      if (!workspaceId || !token) {
        setBlockingError(translateI18n('public_form.invalid_link') || 'Link inválido');
        setLoading(false);
        return;
      }
      try {
        const res = await getOrderPublicForm(workspaceId, token);
        const data = res?.data || res;
        if (!data) {
          setBlockingError(translateI18n('public_form.unavailable') || 'Formulário indisponível');
          return;
        }
        setFormData(data);
        const itemV = {};
        const pathAcc = {};
        (data.line_items || []).forEach((li) => {
          itemV[li.item_id] = {};
          (li.customization_fields || []).forEach((f) => {
            if (f.value_type === 'image') {
              const ids = parsePublicImageIds(f.value);
              itemV[li.item_id][f.key] = stringifyPublicImageIds(ids);
              (f.image_assets || []).forEach((a) => {
                if (a.id && a.url) pathAcc[a.id] = a.url;
              });
            } else {
              itemV[li.item_id][f.key] = f.value || '';
            }
          });
        });
        setLineItemValues(itemV);
        if (Object.keys(pathAcc).length) {
          setPublicImagePathById((prev) => ({ ...prev, ...pathAcc }));
        }
      } catch (err) {
        setBlockingError(err.message || (translateI18n('public_form.unavailable') || 'Formulário indisponível'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [workspaceId, token, setLanguage]);

  const handleLineItemFieldChange = (itemId, key, value) => {
    setLineItemValues((prev) => {
      const next = {
        ...prev,
        [itemId]: { ...(prev[itemId] || {}), [key]: value },
      };
      lineItemValuesRef.current = next;
      return next;
    });
  };

  const resolvePublicImageUrl = (mediaId) => {
    const path = publicImagePathById[mediaId];
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  };

  const openPublicImagePicker = (itemId, key) => {
    setActiveImageTarget(itemId, key);
    publicImageFileInputRef.current?.click();
  };

  const uploadPublicImageFile = useCallback(async (file, itemId, key) => {
    if (!file || !workspaceId || !token || !itemId || !key) return false;
    if (!isAcceptedImageFile(file)) {
      setSurfaceError(translate('public_form.invalid_image_type') || 'Formato inválido. Use JPEG, PNG ou WebP.');
      return false;
    }
    const cur = parsePublicImageIds((lineItemValuesRef.current[itemId] || {})[key]);
    if (cur.length >= 10) {
      setSurfaceError(translate('public_form.image_max') || 'Máximo de 10 imagens por campo');
      return false;
    }
    try {
      const res = await uploadOrderPublicFormMedia(workspaceId, token, file);
      const row = res?.data;
      const mediaId = row?.id;
      const filePath = row?.file_path;
      if (!mediaId || !filePath) {
        throw new Error(translate('public_form.upload_failed') || 'Falha ao enviar imagem');
      }
      setPublicImagePathById((prev) => ({ ...prev, [mediaId]: filePath }));
      const next = [...cur, mediaId];
      handleLineItemFieldChange(itemId, key, stringifyPublicImageIds(next));
      return true;
    } catch (err) {
      setSurfaceError(err.message || (translate('public_form.upload_failed') || 'Falha ao enviar imagem'));
      return false;
    }
  }, [workspaceId, token, translate]);

  const uploadPublicImageFiles = useCallback(async (fileList, itemId, key) => {
    if (!fileList?.length || !itemId || !key) return;
    setActiveImageTarget(itemId, key);
    setUploadingImage(true);
    setSurfaceError(null);
    try {
      for (const file of fileList) {
        const cur = parsePublicImageIds((lineItemValuesRef.current[itemId] || {})[key]);
        if (cur.length >= 10) {
          setSurfaceError(translate('public_form.image_max') || 'Máximo de 10 imagens por campo');
          break;
        }
        const ok = await uploadPublicImageFile(file, itemId, key);
        if (!ok) break;
      }
    } finally {
      setUploadingImage(false);
    }
  }, [setActiveImageTarget, uploadPublicImageFile, translate]);

  const handlePublicImageFileChange = async (e) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    const { itemId, key } = publicImageUploadTargetRef.current;
    if (!picked.length || !workspaceId || !token || !itemId || !key) return;
    await uploadPublicImageFiles(picked, itemId, key);
  };

  useEffect(() => {
    if (!formData || success || imageFieldTargets.length === 0) return undefined;

    const handlePaste = (e) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea') return;

      const files = extractImageFilesFromDataTransfer(e.clipboardData);
      if (!files.length) return;

      e.preventDefault();
      let { itemId, key } = publicImageUploadTargetRef.current;
      if (!itemId || !key) {
        if (imageFieldTargets.length === 1) {
          itemId = imageFieldTargets[0].itemId;
          key = imageFieldTargets[0].key;
        } else {
          setSurfaceError(translate('public_form.image_paste_target') || 'Clique em um campo de imagem antes de colar');
          return;
        }
      }
      uploadPublicImageFiles(files, itemId, key);
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [formData, success, imageFieldTargets, uploadPublicImageFiles, translate]);

  const removePublicImage = (itemId, key, mediaId) => {
    const cur = parsePublicImageIds((lineItemValues[itemId] || {})[key]);
    const next = cur.filter((x) => x !== mediaId);
    handleLineItemFieldChange(itemId, key, stringifyPublicImageIds(next));
  };

  const handleSubmit = async () => {
    if (!workspaceId || !token || !formData) return;
    setSubmitting(true);
    setSurfaceError(null);
    try {
      const lineItems = (formData.line_items || []).map((li) => ({
        line_id: li.line_id,
        item_id: li.item_id,
        customization_fields: (li.customization_fields || []).map((f) => {
          let val = (lineItemValues[li.item_id] || {})[f.key] ?? '';
          if (f.value_type === 'image') {
            val = val == null || String(val).trim() === '' ? '[]' : String(val).trim();
          }
          return {
            key: f.key,
            name: f.name,
            value_type: f.value_type,
            value: val,
          };
        }),
      }));
      await submitOrderPublicForm(workspaceId, token, {
        order_customization_fields: [],
        line_items: lineItems,
      });
      setSuccess(true);
    } catch (err) {
      setSurfaceError(err.message || (translate('public_form.submit_error') || 'Erro ao enviar'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field, value, onChange) => {
    const val = value ?? '';
    if (field.value_type === 'textarea') {
      return (
        <TextField
          fullWidth
          size="small"
          label={field.name}
          value={val}
          onChange={(e) => onChange(e.target.value)}
          multiline
          rows={3}
          sx={{ mt: 1 }}
        />
      );
    }
    if (field.value_type === 'number') {
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          label={field.name}
          value={val}
          onChange={(e) => onChange(e.target.value)}
          sx={{ mt: 1 }}
        />
      );
    }
    if (field.value_type === 'date') {
      return (
        <TextField
          fullWidth
          size="small"
          type="date"
          label={field.name}
          value={val}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 1 }}
        />
      );
    }
    if (field.value_type === 'select' && field.options && field.options.length > 0) {
      const options = [...field.options];
      if (field.allow_other) {
        options.push(translate('public_form.other') || 'Outro');
      }
      return (
        <TextField
          fullWidth
          size="small"
          select
          label={field.name}
          value={val}
          onChange={(e) => onChange(e.target.value)}
          sx={{ mt: 1 }}
        >
          <MenuItem value="">
            <em>{translate('public_form.select') || 'Selecione'}</em>
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    return (
      <TextField
        fullWidth
        size="small"
        label={field.name}
        value={val}
        onChange={(e) => onChange(e.target.value)}
        sx={{ mt: 1 }}
      />
    );
  };

  const renderImageField = (li, f) => {
    const rawVal = (lineItemValues[li.item_id] || {})[f.key];
    const ids = parsePublicImageIds(rawVal);
    const fieldKey = imageFieldKey(li.item_id, f.key);
    const isDragOver = dragOverImageKey === fieldKey;

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        e.dataTransfer.dropEffect = 'copy';
      } catch {
        /* ignore */
      }
      setActiveImageTarget(li.item_id, f.key);
      setDragOverImageKey(fieldKey);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const related = e.relatedTarget;
      if (related && e.currentTarget.contains(related)) return;
      if (dragOverImageKey === fieldKey) setDragOverImageKey(null);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverImageKey(null);
      if (uploadingImage || ids.length >= 10) return;
      const files = extractImageFilesFromDataTransfer(e.dataTransfer);
      if (files.length) {
        uploadPublicImageFiles(files, li.item_id, f.key);
      }
    };

    return (
      <Box
        onClick={() => setActiveImageTarget(li.item_id, f.key)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          mt: 1,
          p: 1.5,
          borderRadius: 1,
          border: '2px dashed',
          borderColor: isDragOver ? 'primary.main' : 'primary.main',
          bgcolor: isDragOver ? 'action.selected' : 'action.hover',
          transition: 'background-color 0.15s ease',
          cursor: 'default',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ImageOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold">
            {f.name}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {translate('public_form.image_hint') || 'JPEG, PNG ou WebP — clique, arraste ou cole (Ctrl+V)'}
        </Typography>
        <input
          ref={publicImageFileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          hidden
          onChange={handlePublicImageFileChange}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={uploadingImage || ids.length >= 10}
          onClick={(e) => {
            e.stopPropagation();
            openPublicImagePicker(li.item_id, f.key);
          }}
        >
          {uploadingImage ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          {translate('public_form.add_images') || 'Adicionar imagens'}
        </Button>
        {ids.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
            {ids.map((mid) => {
              const src = resolvePublicImageUrl(mid);
              return (
                <Box
                  key={mid}
                  sx={{
                    position: 'relative',
                    width: 96,
                    height: 96,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  {src ? (
                    <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageOutlinedIcon color="disabled" />
                    </Box>
                  )}
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'background.paper' }}
                    onClick={() => removePublicImage(li.item_id, f.key, mid)}
                    aria-label="remove"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  const pageShell = (content) => (
    <>
      <Box sx={{ position: 'fixed', top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 16 }, zIndex: 10 }}>
        <ThemeModeToggle />
      </Box>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          py: 4,
          px: 2,
        }}
      >
        <Box sx={{ maxWidth: 640, mx: 'auto' }}>
          {content}
        </Box>
      </Box>
    </>
  );

  if (loading) {
    return pageShell(
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (blockingError) {
    return pageShell(
      <Box>
        <Alert severity="error">{blockingError}</Alert>
      </Box>
    );
  }

  if (success) {
    return pageShell(
      <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="success.main" gutterBottom>
          {translate('public_form.success') || 'Formulário enviado com sucesso!'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {translate('public_form.success_message') || 'Obrigado. Suas informações foram recebidas.'}
        </Typography>
      </Paper>
    );
  }

  const lineItemsWithFields = (formData?.line_items || []).filter((li) => (li.customization_fields || []).length > 0);
  const hasLineItems = lineItemsWithFields.length > 0;

  const orderHeaderText = (() => {
    if (!formData) {
      return '';
    }
    const fromTitle = formData.title && String(formData.title).trim();
    if (fromTitle) {
      return fromTitle;
    }
    return (formData.customer_name && String(formData.customer_name).trim()) || '';
  })();

  return pageShell(
    <Box sx={{ position: 'relative' }}>
      <SavingOverlay open={submitting} />
      <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {orderHeaderText}
        </Typography>
      </Paper>

      {surfaceError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSurfaceError(null)}>
          {surfaceError}
        </Alert>
      )}

      {hasLineItems &&
        lineItemsWithFields.map((li) => (
          <Paper key={li.item_id} elevation={2} sx={{ p: 3, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {li.block_title || li.product_name || li.item_description || `Item`}
              {li.quantity > 1 && ` (${li.quantity}x)`}
            </Typography>
            {(li.customization_fields || []).map((f) => (
              <Box key={f.key}>
                {f.value_type === 'image'
                  ? renderImageField(li, f)
                  : renderField(f, (lineItemValues[li.item_id] || {})[f.key], (v) => handleLineItemFieldChange(li.item_id, f.key, v))}
              </Box>
            ))}
          </Paper>
        ))}

      {!hasLineItems && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {translate('public_form.no_fields') || 'Nenhum campo de customização para preencher.'}
          </Typography>
        </Paper>
      )}

      {hasLineItems && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {submitting ? (translate('common.saving') || 'Enviando...') : (translate('public_form.submit') || 'Enviar')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default OrderPublicFormPage;
